<template>
  <div class="bg-signableBrandColours-white">
    <div class="relative z-10">
      <button
        v-show="scrollLeft"
        @click="scrollTabsLeft"
        :aria-label="$t('library.tabs.tabList.scrollLeftAria')"
        class="z-10 group hover:text-signableBrandColours-black active:text-signableBrandColours-midGrey cursor-pointer flex items-center p-sm pr-0 border-0 border-b-1 border-solid border-border bg-signableBrandColours-white absolute -left-px top-0 h-full text-signableBrandColours-midGrey"
      >
        <div class="flex items-center p-xs border-signableBrandColours-midGrey group-hover:border-signableBrandColours-black group-active:border-signableBrandColours-midGrey border-solid border border-t-0 border-l-0 border-b-0">
          <ChevronUpIcon class="-rotate-90" />
        </div>
      </button>
      <div
        @scroll="manageScroll"
        ref="tabContainer"
        class="no-scrollbar flex overflow-hidden overflow-x-scroll whitespace-nowrap list-none pl-none m-0"
      >
        <TabItem
          v-for="tab in tabs"
          :key="tab.id"
          ref="tabs"
          v-bind="tab"
          :selected="tab.id === value"
          @selected="selectTab"
          @dismissed="dismissTab"
          class="sm:first:ml-sm"
          data-test="tabList"
        />
      </div>
      <button
        v-show="scrollRight"
        @click="scrollTabsRight"
        :aria-label="$t('library.tabs.tabList.scrollRightAria')"
        class="z-10 group hover:text-signableBrandColours-black active:text-signableBrandColours-midGrey cursor-pointer flex items-center p-sm pl-0 border-0 border-b-1 border-solid border-border bg-signableBrandColours-white absolute -right-px top-0 h-full text-signableBrandColours-midGrey"
      >
        <div class="flex items-center p-xs border-signableBrandColours-midGrey group-hover:border-signableBrandColours-black group-active:border-signableBrandColours-midGrey border-solid border border-t-0 border-r-0 border-b-0">
          <ChevronUpIcon class="rotate-90" />
        </div>
      </button>
    </div>
    <hr class="relative -mt-{1px} mb-md z-0" />
  </div>
</template>

<script>
// @ts-check
import { defineComponent, onMounted, onUnmounted, ref, watch } from 'vue';

import { ChevronUpIcon } from '@/components/library/icons';
import { TabItem } from '@/components/library/tabs/TabItem';

/** @typedef {Omit<import('@/components/library/tabs/TabItem').TTab, 'selected'>} TTab */

export default defineComponent({
  components: { TabItem, ChevronUpIcon },
  props: {
    /** @type {import('vue').PropOptions<TTab[]>} */
    tabs: {
      type: Array,
      required: true,
    },
    value: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const scrollLeft = ref(false);
    const scrollRight = ref(false);

    /** @type {import('vue').Ref<HTMLElement | null>} */
    const tabContainer = ref(null);

    function manageScroll() {
      if (tabContainer.value) {
        scrollLeft.value = tabContainer.value.scrollLeft > 10;
        scrollRight.value =
          tabContainer.value.scrollLeft + tabContainer.value.offsetWidth + 10 <=
          tabContainer.value.scrollWidth;
      }
    }

    function scrollTabsLeft() {
      if (tabContainer.value) {
        tabContainer.value.scrollLeft -= 100;
        manageScroll();
      }
    }

    function scrollTabsRight() {
      if (tabContainer.value) {
        tabContainer.value.scrollLeft += 100;
        manageScroll();
      }
    }

    /** @param {string} id */
    function selectTab(id) {
      emit('input', id);
    }

    /** @param {string} id */
    function dismissTab(id) {
      emit('dismiss', id);
    }

    watch(
      () => props.tabs,
      () => {
        if (!props.tabs.some(tab => tab.id === props.value)) {
          emit('input', props.tabs[0].id);
        }
        manageScroll();
      },
      { immediate: true },
    );

    onMounted(() => {
      window.addEventListener('resize', manageScroll);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', manageScroll);
    });

    return {
      scrollLeft,
      scrollRight,
      tabContainer,
      manageScroll,
      scrollTabsLeft,
      scrollTabsRight,
      selectTab,
      dismissTab,
    };
  },
});
</script>
